import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/features/auth/auth-shell";
import { api, ApiError } from "@/lib/api";

type HandoffMeta = {
  provider: string;
  label: string;
  description?: string;
  fields: Array<{
    key: string;
    label: string;
    secret: boolean;
    required: boolean;
    help?: string | null;
  }>;
  expiresAt: number;
};

export function CredentialHandoffPage() {
  const [params] = useSearchParams();
  const token = params.get("token")?.trim() ?? "";
  const [meta, setMeta] = useState<HandoffMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("This setup link is missing a token.");
      return;
    }
    let cancelled = false;
    void api<HandoffMeta>(
      `/api/credential-handoff/${encodeURIComponent(token)}`,
    )
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? e.message
              : "This setup link is invalid or expired.",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!meta || !token || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/api/credential-handoff/${encodeURIComponent(token)}`, {
        method: "POST",
        body: JSON.stringify({ secrets }),
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not save credentials",
      );
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AuthShell title="Destination Setup">
        <p className="text-sm text-rs-ink-muted">Loading setup link…</p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Destination Connected">
        <p className="text-sm text-rs-ink-muted">
          Credentials were saved for this AttendeeSync workspace. You can close
          this page.
        </p>
        <Button asChild className="mt-4">
          <Link to="/login">Go To Sign In</Link>
        </Button>
      </AuthShell>
    );
  }

  if (!meta) {
    return (
      <AuthShell title="Setup Link Unavailable">
        <p className="text-sm text-rs-danger">
          {error ?? "This setup link is invalid or expired."}
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Connect ${meta.label}`}
      description={
        meta.description ??
        "Paste the CRM credentials for this AttendeeSync workspace. This one-time link does not require an account."
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        {meta.fields.map((field) => (
          <div key={field.key} className="space-y-1.5">
            <Label htmlFor={field.key}>
              {field.label}
              {field.required ? "" : " (optional)"}
            </Label>
            <div className="relative">
              <Input
                id={field.key}
                name={field.key}
                type={
                  field.secret && !revealed[field.key] ? "password" : "text"
                }
                required={field.required}
                value={secrets[field.key] ?? ""}
                onChange={(e) =>
                  setSecrets((prev) => ({
                    ...prev,
                    [field.key]: e.target.value,
                  }))
                }
                className={field.secret ? "pr-10" : undefined}
              />
              {field.secret ? (
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400"
                  onClick={() =>
                    setRevealed((prev) => ({
                      ...prev,
                      [field.key]: !prev[field.key],
                    }))
                  }
                  aria-label={revealed[field.key] ? "Hide Value" : "Show Value"}
                >
                  {revealed[field.key] ? (
                    <EyeOff className="size-4" />
                  ) : (
                    <Eye className="size-4" />
                  )}
                </button>
              ) : null}
            </div>
            {field.help ? (
              <p className="text-xs text-rs-ink-muted">{field.help}</p>
            ) : null}
          </div>
        ))}
        {error ? <p className="text-sm text-rs-danger">{error}</p> : null}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? "Saving…" : "Save Destination Credentials"}
        </Button>
      </form>
    </AuthShell>
  );
}
